"use client";

import React, { useState } from "react";
import { Check, Star } from "lucide-react";
import { useParams } from "next/navigation";

import ContactUsDialog from "@/components/dashboard/ContactUsDialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const plans = [
  {
    name: "Free",
    price: 0,
    description: "Pefect for individual users getting started",
    features: ["25 Monthly Credits", "5 Daily Credits"],
    popular: false,
  },
  {
    name: "Pro",
    price: 25,
    description: "Recommended for professionals",
    features: ["250 Monthly Credits", "5 Daily Credits"],
    popular: false,
  },
  {
    name: "Team",
    price: 125,
    description: "For teams collaborating on multiple projects",
    features: [
      "1000 Monthly Credits",
      "5 Team Members",
      "Unlimited Daily Credits",
    ],
    popular: false,
  },
];

interface PricingDialogProps {
  trigger: React.ReactNode;
  currentSubscription?: {
    id?: string | null;
    externalSubscriptionId?: string | null;
    planTierName?: string | null;
  } | null;
}

export default function PricingDialog({
  trigger,
  currentSubscription,
}: PricingDialogProps) {
  const [open, setOpen] = useState(false);
  const params = useParams();
  const orgId = (params as { orgId?: string })?.orgId;

  const hasStripeSubscription = Boolean(
    currentSubscription?.externalSubscriptionId,
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>Choose a plan</DialogTitle>
          <DialogDescription>
            Compare plans and upgrade your subscription.
          </DialogDescription>
        </DialogHeader>
        <section id="pricing" className="py-4">
          <div className="mx-auto max-w-7xl px-2 sm:px-4">
            {/* <div className="mb-8 text-center">
              <p className="mx-auto max-w-3xl text-sm text-muted-foreground">
                Choose the perfect plan for your business. No hidden fees, no
                surprises.
              </p>
            </div> */}

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
              {plans.map((plan, index) => (
                <div
                  key={index}
                  className={`relative flex h-full flex-col rounded-2xl border bg-card/50 p-6 backdrop-blur-sm ${
                    plan.popular
                      ? "border-primary ring-2 ring-primary/20"
                      : "border-border hover:border-border/80"
                  } transition-all duration-300 hover:scale-105 hover:transform`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 transform">
                      <div className="flex items-center space-x-1 whitespace-nowrap rounded-full bg-gradient-to-r from-primary to-tertiary px-6 py-2 text-sm font-medium text-primary-foreground">
                        <Star className="h-4 w-4" />
                        <span>Most Popular</span>
                      </div>
                    </div>
                  )}

                  <div className="mb-6 text-center">
                    <h3 className="mb-2 text-xl font-bold text-foreground">
                      {plan.name}
                    </h3>
                    <div className="mb-3">
                      <span className="text-3xl font-bold text-foreground">
                        ${plan.price}
                      </span>
                      <span className="text-muted-foreground">/month</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {plan.description}
                    </p>
                  </div>

                  <ul className="mb-6 space-y-3 text-sm">
                    {plan.features.map((feature, featureIndex) => (
                      <li
                        key={featureIndex}
                        className="flex items-center space-x-3"
                      >
                        <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-500">
                          <Check className="h-3 w-3 text-white" />
                        </div>
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  {(() => {
                    const name = plan.name?.toLowerCase();
                    const lookup =
                      name === "pro"
                        ? "pro_plan_price_lookup"
                        : name === "team"
                          ? "team_plan_price_lookup"
                          : name === "free"
                            ? "free_plan_price_lookup"
                            : null;
                    if (!lookup) return <div />;

                    const onSwitchPlan = async () => {
                      if (
                        !currentSubscription?.id ||
                        !currentSubscription.externalSubscriptionId
                      ) {
                        return;
                      }
                      await fetch("/api/subscription/switch-plan", {
                        method: "POST",
                        headers: {
                          "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                          orgId,
                          subscriptionId: currentSubscription.id,
                          externalSubscriptionId:
                            currentSubscription.externalSubscriptionId,
                          priceLookupKey: lookup,
                        }),
                      });
                      setOpen(false);
                      if (typeof window !== "undefined") {
                        window.location.reload();
                      }
                    };

                    const onSwitchToFree = async () => {
                      if (
                        !currentSubscription?.id ||
                        !currentSubscription.externalSubscriptionId
                      ) {
                        return;
                      }
                      await fetch("/api/subscription/switch-to-free", {
                        method: "POST",
                        headers: {
                          "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                          subscriptionId: currentSubscription.id,
                          externalSubscriptionId:
                            currentSubscription.externalSubscriptionId,
                        }),
                      });
                      setOpen(false);
                      if (typeof window !== "undefined") {
                        window.location.reload();
                      }
                    };

                    // If we already have a Stripe subscription:
                    // - For paid plans (Pro/Team), use the switch-plan route
                    // - For Free, call switch-to-free to schedule end-of-period downgrade
                    const isPaidPlan = name === "pro" || name === "team";
                    const isFreePlan = name === "free";
                    if (hasStripeSubscription && isPaidPlan) {
                      return (
                        <div className="mt-auto self-center">
                          <Button
                            type="button"
                            onClick={onSwitchPlan}
                            id="switch-plan-button"
                            className="group flex transform items-center space-x-2 rounded-xl bg-gradient-to-r from-blue-700 to-purple-700 px-6 py-3 text-sm font-semibold text-white transition-all duration-200 hover:scale-105 hover:from-blue-600 hover:to-purple-600 hover:opacity-90"
                          >
                            Switch Plan
                          </Button>
                        </div>
                      );
                    } else if (hasStripeSubscription && isFreePlan) {
                      return (
                        <div className="mt-auto self-center">
                          <Button
                            type="button"
                            onClick={onSwitchToFree}
                            id="switch-to-free-button"
                            className="group flex transform items-center space-x-2 rounded-xl bg-gradient-to-r from-blue-700 to-purple-700 px-6 py-3 text-sm font-semibold text-white transition-all duration-200 hover:scale-105 hover:from-blue-600 hover:to-purple-600 hover:opacity-90"
                          >
                            Switch to Free
                          </Button>
                        </div>
                      );
                    }

                    // No Stripe subscription yet (e.g. free internal plan) or free
                    // Stripe plan: use Checkout to create the first subscription.
                    return (
                      <form
                        action={
                          orgId
                            ? `/${orgId}/admin/subscription/create-checkout-session`
                            : "/admin/subscription/create-checkout-session"
                        }
                        method="POST"
                        className="mt-auto self-center"
                      >
                        <input type="hidden" name="lookup_key" value={lookup} />
                        <Button
                          type="submit"
                          id="checkout-and-portal-button"
                          className="group flex transform items-center space-x-2 rounded-xl bg-gradient-to-r from-blue-700 to-purple-700 px-6 py-3 text-sm font-semibold text-white transition-all duration-200 hover:scale-105 hover:from-blue-600 hover:to-purple-600 hover:opacity-90"
                        >
                          Switch Plan
                        </Button>
                      </form>
                    );
                  })()}
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <p className="mb-3 text-sm text-muted-foreground">
                Need a custom solution? Contact us for enterprise pricing.
              </p>
              <ContactUsDialog
                trigger={
                  <Button
                    variant="ghost"
                    className="border border-white/20 text-xs text-white/70 transition-colors hover:bg-white/10 hover:text-white"
                  >
                    Contact Us →
                  </Button>
                }
              />
            </div>
          </div>
        </section>
      </DialogContent>
    </Dialog>
  );
}
